import React, { useEffect, useState } from 'react';
import { Input, Button, Typography, theme } from 'antd';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { IconSparkles, IconInfoCircle } from '@tabler/icons-react';
import { message } from '../../services/toastMessage';
import { createQuoteDraft } from '../../services/QuoteDraftService';
import { IQuoteDraftLine } from '../../interfaces/heavyDutyMachinery/IQuoteDraft';
import { TRANSLATIONS } from '../../constants/translations';
import { translate } from '../../helpers/translationHelper';
import { useSpeechDictation } from '../../hooks/useSpeechDictation';
import { TEAL, TEAL_SOFT_BG } from '../../theme/creamyTokens';
import DictateButton from '../DictateButton';

const { TextArea } = Input;
const { Text } = Typography;

/** Same floor the backend refuses below -- checked here so a three-word job never costs a round trip. */
const MIN_DESCRIPTION_LENGTH = 10;

interface QuoteDraftAssistantProps {
  /**
   * Called with the lines the assistant produced. The caller decides what to
   * do with them -- this component never touches the quote itself, which is
   * what lets it sit inside any editor without knowing which one.
   */
  onDrafted: (lines: IQuoteDraftLine[], notes?: string) => void;
  /** Prefilled trade hint, when the caller already knows it (a service booking's own category, say). */
  defaultTrade?: string;
  /**
   * The job already described by somebody else -- a customer's own words on a
   * quote request. Prefilled rather than asked for again: retyping what the
   * customer already wrote is the exact work this is meant to remove, and
   * they described it better than a summary would.
   */
  defaultJob?: string;
  /** True once the caller is holding assistant-made lines, so the button can say "again". */
  hasDraft?: boolean;
}

/**
 * Describe the job in words, get the lines.
 *
 * The one piece the provider actually touches of the whole rate-card feature:
 * they wrote their prices down once, and from then on a quote is a sentence.
 * What comes back is priced off their own catalogue wherever it can be (see
 * the backend's QuoteDraftsController), and honestly labelled as a guess
 * wherever it cannot.
 *
 * Nothing here commits anything. It hands lines to whoever is holding the
 * editor and stops -- the provider still reads them, fixes them and decides
 * to send them, which is the whole reason this is a draft and not an
 * automation.
 */
const QuoteDraftAssistant: React.FC<QuoteDraftAssistantProps> = ({ onDrafted, defaultTrade, defaultJob, hasDraft }) => {
  const { t, i18n } = useTranslation();
  const { token } = theme.useToken();
  const dictation = useSpeechDictation(i18n.language);

  const [description, setDescription] = useState(defaultJob ?? '');
  const [trade, setTrade] = useState(defaultTrade ?? '');
  const [working, setWorking] = useState(false);
  const [notes, setNotes] = useState<string>();
  // Set once the backend says the assistant is not configured here. The whole
  // block then puts itself away instead of offering a button that cannot work.
  const [unavailable, setUnavailable] = useState(false);

  // Dictated words land in the field rather than going straight off to the
  // model: a recogniser mishears, and the provider has to be able to see what
  // it heard before it costs them a round trip.
  useEffect(() => {
    if (dictation.transcript) setDescription(dictation.transcript);
  }, [dictation.transcript]);

  useEffect(() => {
    if (dictation.error) message.error(translate(t, TRANSLATIONS.DICTATE_ERROR));
  }, [dictation.error, t]);

  const handleGenerate = async () => {
    const job = description.trim();
    if (job.length < MIN_DESCRIPTION_LENGTH) {
      message.error(translate(t, TRANSLATIONS.QUOTE_DRAFT_TOO_SHORT));
      return;
    }

    dictation.stop();
    setWorking(true);
    try {
      const outcome = await createQuoteDraft({ description: job, trade: trade.trim() || undefined });
      if (!outcome.ok) {
        if (outcome.unavailable) {
          setUnavailable(true);
          return;
        }
        if (outcome.error) console.error('Quote draft failed:', outcome.error);
        message.error(translate(t, TRANSLATIONS.QUOTE_DRAFT_ERROR));
        return;
      }

      if (outcome.draft.lines.length === 0) {
        // A valid answer that happens to be empty: the model understood the
        // job and had nothing to put on it. Saying so beats an empty editor
        // that looks broken.
        setNotes(outcome.draft.notes);
        message.info(translate(t, TRANSLATIONS.QUOTE_DRAFT_EMPTY));
        return;
      }

      setNotes(outcome.draft.notes);
      onDrafted(outcome.draft.lines, outcome.draft.notes);
    } finally {
      setWorking(false);
    }
  };

  if (unavailable) {
    return (
      <div style={{ display: 'flex', gap: 8, padding: '10px 12px', borderRadius: 12, background: token.colorFillQuaternary, marginBottom: 14 }}>
        <IconInfoCircle size={15} style={{ flexShrink: 0, marginTop: 2, color: token.colorTextTertiary }} />
        <Text type="secondary" style={{ fontSize: 12 }}>
          {translate(t, TRANSLATIONS.QUOTE_DRAFT_UNAVAILABLE)}
        </Text>
      </div>
    );
  }

  return (
    <div
      style={{
        padding: 12,
        marginBottom: 14,
        borderRadius: 14,
        background: TEAL_SOFT_BG,
      }}
    >
      <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 8 }}>
        <IconSparkles size={15} style={{ color: TEAL }} />
        <Text strong style={{ fontSize: 13 }}>
          {translate(t, TRANSLATIONS.QUOTE_DRAFT_TITLE)}
        </Text>
        {/* Next to the field it fills, and pushed to the end so the thumb
            finds it in the same place it finds every other microphone. */}
        <span style={{ marginLeft: 'auto' }}>
          <DictateButton
            supported={dictation.supported}
            listening={dictation.listening}
            onStart={() => dictation.start(description)}
            onStop={dictation.stop}
            disabled={working}
          />
        </span>
      </div>

      <TextArea
        rows={3}
        value={description}
        onChange={(e) => setDescription(e.target.value)}
        placeholder={translate(
          t,
          dictation.listening ? TRANSLATIONS.QUOTE_DRAFT_LISTENING : TRANSLATIONS.QUOTE_DRAFT_DESCRIPTION_PLACEHOLDER
        )}
        maxLength={4000}
        style={{ borderRadius: 12, marginBottom: 8 }}
      />

      <Input
        value={trade}
        onChange={(e) => setTrade(e.target.value)}
        placeholder={translate(t, TRANSLATIONS.QUOTE_DRAFT_TRADE_PLACEHOLDER)}
        maxLength={80}
        style={{ borderRadius: 999, marginBottom: 8 }}
      />

      <Button
        block
        type="primary"
        icon={<IconSparkles size={15} />}
        loading={working}
        onClick={handleGenerate}
        style={{ borderRadius: 999 }}
      >
        {translate(t, hasDraft ? TRANSLATIONS.QUOTE_DRAFT_REGENERATE : TRANSLATIONS.QUOTE_DRAFT_GENERATE)}
      </Button>

      {/* Worth its space: a wrong assumption stated out loud is the cheapest
          way to notice the assistant understood a different job entirely, and
          it costs one sentence. */}
      {notes && (
        <div style={{ marginTop: 10, padding: '8px 10px', borderRadius: 10, background: token.colorBgContainer }}>
          <Text type="secondary" style={{ fontSize: 11, display: 'block', marginBottom: 2 }}>
            {translate(t, TRANSLATIONS.QUOTE_DRAFT_NOTES_TITLE)}
          </Text>
          <Text style={{ fontSize: 12 }}>{notes}</Text>
        </div>
      )}

      <Text type="secondary" style={{ fontSize: 11, display: 'block', marginTop: 8 }}>
        {translate(t, TRANSLATIONS.QUOTE_DRAFT_CATALOG_HINT)}{' '}
        <Link to="/heavy-duty-machinery/my-rates" style={{ color: TEAL }}>
          {translate(t, TRANSLATIONS.QUOTE_DRAFT_CATALOG_LINK)}
        </Link>
      </Text>
    </div>
  );
};

export default QuoteDraftAssistant;
